import { doc, setDoc, getDoc, updateDoc, collection, serverTimestamp } from "firebase/firestore";
import { storage, firestore, auth } from "../firebase/firebaseConfig";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getUserIPInfo } from "./ipService";


/**
 * Uploads a file to Firebase Storage and returns the download URL.
 * @param {string} userId - User ID.
 * @param {File} file - The file to upload.
 * @param {string} fileName - Name to store the file.
 */
const uploadFile = async (userId, file, fileName) => {
  if (!file) return null;
  
  const storageRef = ref(storage, `kyc_submissions/${userId}/${fileName}`);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
};

/**
 * Submits a KYC application.
 * @param {string} userId - User ID.
 * @param {Object} kycData - KYC details including documents.
 */
export const submitKYC = async (userId, kycData) => {
  try {
    const ipInfo = await getUserIPInfo();

    // Upload files to Firebase Storage
    const [idFrontURL, idBackURL, selfieURL, proofOfAddressURL] = await Promise.all([
      uploadFile(userId, kycData.idFront, "id_front.jpg"),
      uploadFile(userId, kycData.idBack, "id_back.jpg"),
      uploadFile(userId, kycData.selfie, "selfie.jpg"),
      uploadFile(userId, kycData.proofOfAddress, "proof_of_address.jpg"),
    ]);

    // KYC Data
    const kycPayload = {
      userId,
      fullName: kycData.fullName,
      phone: kycData.phone,
      dob: kycData.dob,
      address: kycData.address,
      country: kycData.country,
      govIdType: kycData.govIdType,
      idFrontURL,
      idBackURL,
      selfieURL,
      proofOfAddressURL,
      ipInfo,
      kycStatus: "pending",
      submittedAt: serverTimestamp(),
    };

    // Store KYC submission in a new collection
    await setDoc(doc(collection(firestore, "kyc_submissions"), userId), kycPayload);

    // Update user profile with KYC status
    await updateDoc(doc(firestore, "users", userId), { kycStatus: "pending" });

    console.log(`[submitKYC] KYC submitted successfully for user ${userId}`);
    return { success: true };
  } catch (error) {
    console.error("[submitKYC] KYC Submission Error:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Tracks user login & flags suspicious activity.
 */
export const trackUserLogin = async () => {
  const user = auth.currentUser;
  if (!user) return;

  try {
    const userRef = doc(firestore, "users", user.uid);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) return;

    const userData = userSnap.data();
    const ipInfo = await getUserIPInfo();

    // If IP country changes, flag for review
    if (userData.ipInfo?.country && userData.ipInfo.country !== ipInfo.country) {
      console.warn(`⚠️ Location Change Detected: ${userData.ipInfo.country} → ${ipInfo.country}`);
      await updateDoc(userRef, { lastLoginIpInfo: ipInfo, flaggedForReview: true });
    } else {
      await updateDoc(userRef, { lastLoginIpInfo: ipInfo });
    }
  } catch (error) {
    console.error("[trackUserLogin] Error:", error);
  }
};


/**
 * Verifies KYC details before approval.
 * @param {Object} kycData - KYC submission data.
 */
export const verifyKYC = (kycData) => {
    const { country: declaredCountry, ipInfo } = kycData;
    const ipCountry = ipInfo?.country;
  
    if (ipCountry && ipCountry !== declaredCountry) {
      console.warn(`⚠️ Location mismatch: Declared ${declaredCountry} vs IP ${ipCountry}`);
      return { status: "review", reason: "Location mismatch" };
    } else {
      return { status: "approved" };
    }
  };
  

  
/**
 * Fetches the KYC submission status for a user.
 * @param {string} userId - The unique ID of the user.
 * @returns {Promise<string|null>} The KYC status ("approved", "pending", etc.) or null if no submission exists.
 */
export const fetchKYCStatus = async (userId) => {
  try {
    const kycRef = doc(firestore, "kyc_submissions", userId);
    const kycSnap = await getDoc(kycRef);

    if (!kycSnap.exists()) {
      console.warn("[KYCService] No KYC submission found for user:", userId);
      return null;
    }

    const kycData = kycSnap.data();
    return kycData.status || null;
  } catch (error) {
    console.error("[KYCService] Error fetching KYC status:", error);
    throw new Error("Failed to fetch KYC status.");
  }
};
